import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  Link2,
  QrCode,
  BarChart3,
  CreditCard,
  Code2,
  Shield,
  Settings,
  LogOut,
  Menu,
  X,
  ChevronDown,
  ShieldCheck,
  Globe,
  Users,
  LifeBuoy,
} from "lucide-react";
import Logo from "@/components/Logo";
import ThemeToggle from "@/components/ThemeToggle";
import LanguageToggle from "@/components/LanguageToggle";
import SoundToggle from "@/components/SoundToggle";
import NotificationBell from "@/components/NotificationBell";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";

const nav = [
  { to: "/app", key: "dash.overview", icon: LayoutDashboard, testid: "nav-overview" },
  { to: "/app/links", key: "dash.links", icon: Link2, testid: "nav-links" },
  { to: "/app/qr", key: null, label: "QR Codes", icon: QrCode, testid: "nav-qr" },
  { to: "/app/protection", key: null, label: "Protection", icon: ShieldCheck, testid: "nav-protection" },
  { to: "/app/developers", key: null, label: "Developers", icon: Code2, testid: "nav-developers" },
  { to: "/app/domains", key: null, label: "Domains", icon: Globe, testid: "nav-domains", adminWs: true },
  { to: "/app/team", key: null, label: "Team", icon: Users, testid: "nav-team", adminWs: true },
  { to: "/app/billing", key: null, label: "Billing", icon: CreditCard, testid: "nav-billing", billingOnly: true },
  { to: "/app/support", key: null, label: "Support", icon: LifeBuoy, testid: "nav-support" },
  { to: "/app/settings", key: "dash.settings", icon: Settings, testid: "nav-settings" },
];

export default function DashboardLayout({ children }) {
  const { user, logout, workspace, workspaces, switchWorkspace } = useAuth();
  const { t } = useI18n();
  const location = useLocation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const isActive = (to) =>
    to === "/app" ? location.pathname === "/app" : location.pathname.startsWith(to);

  const initials = (user?.name || "U").slice(0, 2).toUpperCase();

  const SidebarInner = (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center px-5">
        <Link to="/app" data-testid="sidebar-logo-link">
          <Logo />
        </Link>
      </div>

      <div className="px-3 pb-2">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              data-testid="workspace-switcher-btn"
              className="flex w-full items-center justify-between rounded-[4px] border-[2.5px] border-[hsl(var(--nb-border))] bg-card px-3 py-2.5 text-left shadow-[3px_3px_0_0_hsl(var(--nb-shadow))] transition-colors hover:bg-accent"
            >
              <div className="min-w-0">
                <p className="truncate text-sm font-semibold">{workspace?.name || "Workspace"}</p>
                <p className="text-xs capitalize text-muted-foreground">{workspace?.plan || "free"} plan</p>
              </div>
              <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className="w-56" align="start">
            <DropdownMenuLabel>Workspaces</DropdownMenuLabel>
            <DropdownMenuSeparator />
            {workspaces.map((ws) => (
              <DropdownMenuItem
                key={ws.id}
                data-testid={`workspace-option-${ws.id}`}
                onClick={() => switchWorkspace(ws)}
              >
                {ws.name}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <nav className="flex-1 space-y-1 px-3 py-2">
        {nav
          .filter((item) => !item.billingOnly || ["owner", "admin", "billing_manager"].includes(workspace?.role))
          .filter((item) => !item.adminWs || ["owner", "admin"].includes(workspace?.role))
          .filter((item) => !item.adminOnly || user?.role === "admin")
          .map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.to}
              to={item.to}
              data-testid={item.testid}
              onClick={() => setOpen(false)}
              className={`flex items-center gap-3 rounded-[4px] border-[2.5px] px-3 py-2.5 font-display text-[13px] font-bold uppercase tracking-wide transition-all ${
                isActive(item.to)
                  ? "border-[hsl(var(--nb-border))] bg-primary text-primary-foreground shadow-[3px_3px_0_0_hsl(var(--nb-shadow))]"
                  : "border-transparent text-muted-foreground hover:border-[hsl(var(--nb-border))] hover:bg-accent hover:text-foreground"
              }`}
            >
              <Icon className="h-[18px] w-[18px]" />
              {item.key ? t(item.key) : item.label}
            </Link>
          );
        })}
      </nav>

      <div className="border-t-[3px] border-[hsl(var(--nb-border))] p-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <LanguageToggle />
          <ThemeToggle />
          <SoundToggle />
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop sidebar */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 border-r-[3px] border-[hsl(var(--nb-border))] bg-card lg:block">
        {SidebarInner}
      </aside>

      {/* Mobile sidebar */}
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={() => setOpen(false)} />
          <aside className="absolute inset-y-0 left-0 w-64 border-r-[3px] border-[hsl(var(--nb-border))] bg-card">
            {SidebarInner}
          </aside>
        </div>
      )}

      <div className="lg:pl-64">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b-[3px] border-[hsl(var(--nb-border))] bg-background/85 px-4 backdrop-blur-xl sm:px-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden"
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-btn"
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className="flex items-center gap-2 rounded-full outline-none"
                data-testid="user-menu-btn"
              >
                <Avatar className="h-9 w-9">
                  <AvatarFallback className="bg-primary text-xs font-semibold text-primary-foreground">
                    {initials}
                  </AvatarFallback>
                </Avatar>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel className="truncate">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/app/settings")} data-testid="menu-settings">
                <Settings className="mr-2 h-4 w-4" />
                {t("dash.settings")}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="mr-2 h-4 w-4" />
                {t("dash.logout")}
              </DropdownMenuItem>
            </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8">{children}</main>
      </div>
    </div>
  );
}
